import { useId, type PropsWithChildren, type ReactNode } from 'react';

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

export function ErrorNotice({
  error,
  title = 'Something went wrong',
}: {
  error: unknown;
  title?: string;
}) {
  return (
    <div className="notice notice-error" role="alert">
      <strong>{title}</strong>
      <span>{error instanceof Error ? error.message : 'Please try again.'}</span>
    </div>
  );
}

export function EmptyState({ title, children }: PropsWithChildren<{ title: string }>) {
  return (
    <div className="empty-state">
      <span className="empty-icon" aria-hidden="true">
        ✓
      </span>
      <strong>{title}</strong>
      <p>{children}</p>
    </div>
  );
}

export function ProgressBar({
  value,
  max,
  label,
  compact = false,
}: {
  value: number;
  max: number;
  label: string;
  compact?: boolean;
}) {
  const percentage = max <= 0 ? 0 : Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div className={compact ? 'progress progress-compact' : 'progress'}>
      <div className="progress-label">
        <span>{label}</span>
        <strong>
          {value}/{max}
        </strong>
      </div>
      <div
        className="progress-track"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={value}
      >
        <span style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

export function StatusPill({
  tone,
  children,
}: PropsWithChildren<{
  tone: 'success' | 'warning' | 'danger' | 'neutral' | 'info';
}>) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

export function Section({
  title,
  action,
  children,
  className = '',
}: PropsWithChildren<{ title: string; action?: ReactNode; className?: string }>) {
  const headingId = useId();
  return (
    <section className={`panel ${className}`.trim()} aria-labelledby={headingId}>
      <div className="section-heading">
        <h2 id={headingId}>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function formatNzDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export function relativeNzTime(value: string | null | undefined): string {
  if (!value) return 'Not visited';
  const difference = Date.now() - Date.parse(value);
  const days = Math.floor(Math.abs(difference) / 86_400_000);
  if (days === 0) {
    const hours = Math.floor(Math.abs(difference) / 3_600_000);
    if (hours === 0) return 'Visited less than an hour ago';
    return `Visited ${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  return `${difference >= 0 ? 'Visited' : 'Visits'} ${days} day${days === 1 ? '' : 's'} ${
    difference >= 0 ? 'ago' : 'from now'
  }`;
}

export function numberValue(value: unknown): number {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
}

export function stringValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value.toString();
  }
  return '';
}

export function formString(form: FormData, key: string): string {
  const value = form.get(key);
  return typeof value === 'string' ? value : '';
}
